import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import LiquidGlass from "@/components/ui/LiquidGlass";

// Colores del diseño VettConsult - Igual que desktop
const colors = {
  gradientStart: "#014D43",
  gradientEnd: "#037D6F",
  turquoise: "#26A69A",
  turquoiseGlow: "rgba(38, 166, 154, 0.3)",
  turquoiseStrongGlow: "rgba(38, 166, 154, 0.7)",
  white: "#FFFFFF",
  whiteTransparent: "rgba(255, 255, 255, 0.7)",
  mobileMenuBackground: "#023d35ff",
  mobileMenuText: "#FFFFFF",
  mobileMenuButtonBorder: "#FFFFFF",
  mobileMenuButtonText: "#FFFFFF",
};

/**
 * Navbar optimizado para móviles iOS 26
 * - Z-index correcto para Liquid Glass UI
 * - Safe-area-insets
 * - LiquidGlass solo cuando scrolled (optimización de memoria)
 * - Todas las funcionalidades del desktop
 * - SIN animaciones de aparición (renderizado directo)
 */
const MobileNavbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Detectar scroll para cambiar estilo del navbar
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Cerrar menú al cambiar ruta
  useEffect(() => {
    setIsMenuOpen(false);
  }, [navigate]);

  // Prevenir scroll del body cuando el menú está abierto
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);

    // Actualizar la URL con el nuevo idioma
    const currentPath = window.location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(en|es|fr)/, "");
    const newPath = `/${lang}${pathWithoutLang || "/"}`;
    navigate(newPath);

    setIsMenuOpen(false);
  };

  const menuItems = [
    { key: "home", path: "/", label: t("navbar.home", "Home") },
    {
      key: "services",
      path: "/services",
      label: t("navbar.services", "Services"),
    },
    { key: "pricing", path: "/pricing", label: t("navbar.pricing", "Pricing") },
    // About Us link removed from mobile menu per request - kept here for reference
    // {
    //   key: "about",
    //   path: `/${i18n.language}/about`,
    //   label: t("navbar.about", "About"),
    // },
    {
      key: "blog",
      path: `/${i18n.language}/blog`,
      label: t("navbar.blog", "Blog"),
    },
    {
      key: "contact",
      path: `/${i18n.language}/contact`,
      label: t("navbar.contact", "Contact"),
    },
  ];

  return (
    <>
      {/* Navbar Fixed - Optimizado para iOS 26 con gradiente VettConsult */}
      <nav
        className="fixed top-0 left-0 right-0 z-[9998] shadow-lg transition-all duration-300"
        style={{
          background: `linear-gradient(160deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
          opacity: scrolled ? 0.98 : 1,
          backdropFilter: scrolled ? "blur(10px)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(10px)" : "none",
          paddingTop: "env(safe-area-inset-top)",
          paddingLeft: "env(safe-area-inset-left)",
          paddingRight: "env(safe-area-inset-right)",
        }}
      >
        {scrolled ? (
          // LiquidGlass activado solo cuando hay scroll (optimización)
          <LiquidGlass
            width={undefined}
            height={undefined}
            borderRadius={0}
            innerShadowColor="#000000"
            innerShadowBlur={11}
            innerShadowSpread={-7}
            glassTintColor="rgba(255, 255, 255, 0.17)"
            glassTintOpacity={17}
            frostBlurRadius={5}
            noiseFrequency={0.013}
            noiseStrength={114}
            className="w-full"
          >
            <div className="max-w-full px-4 py-3.5">
              <div className="flex items-center justify-between">
                {/* Logo */}
                <Link
                  to="/"
                  className="flex items-center space-x-2 text-xl font-bold text-white font-display hover:opacity-80 transition-opacity"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("common.appName", "VettConsult")}
                </Link>

                {/* Hamburger Menu Button */}
                <button
                  onClick={toggleMenu}
                  className="relative z-[9999] p-2 focus:outline-none"
                  aria-label={isMenuOpen ? "Close Menu" : "Open Menu"}
                  type="button"
                  style={{ color: colors.white }}
                >
                  <div className="w-6 h-5 flex flex-col justify-between">
                    <motion.span
                      className="block h-0.5 w-full bg-current"
                      animate={{
                        rotate: isMenuOpen ? 45 : 0,
                        translateY: isMenuOpen ? 8 : 0,
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 260,
                        damping: 20,
                      }}
                    />
                    <motion.span
                      className="block h-0.5 w-full bg-current"
                      animate={{ opacity: isMenuOpen ? 0 : 1 }}
                      transition={{ duration: 0.1 }}
                    />
                    <motion.span
                      className="block h-0.5 w-full bg-current"
                      animate={{
                        rotate: isMenuOpen ? -45 : 0,
                        translateY: isMenuOpen ? -8 : 0,
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 260,
                        damping: 20,
                      }}
                    />
                  </div>
                </button>
              </div>
            </div>
          </LiquidGlass>
        ) : (
          // Navbar con gradiente inicial (sin LiquidGlass para ahorrar memoria)
          <div className="max-w-full px-4 py-3.5">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link
                to="/"
                className="flex items-center space-x-2 text-xl font-bold text-white font-display hover:opacity-80 transition-opacity"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("common.appName", "VettConsult")}
              </Link>

              {/* Hamburger Menu Button */}
              <button
                onClick={toggleMenu}
                className="relative z-[9999] p-2 focus:outline-none"
                aria-label={isMenuOpen ? "Close Menu" : "Open Menu"}
                type="button"
                style={{ color: colors.white }}
              >
                <div className="w-6 h-5 flex flex-col justify-between">
                  <motion.span
                    className="block h-0.5 w-full bg-current"
                    animate={{
                      rotate: isMenuOpen ? 45 : 0,
                      translateY: isMenuOpen ? 8 : 0,
                    }}
                    transition={{ type: "spring", stiffness: 260, damping: 20 }}
                  />
                  <motion.span
                    className="block h-0.5 w-full bg-current"
                    animate={{ opacity: isMenuOpen ? 0 : 1 }}
                    transition={{ duration: 0.1 }}
                  />
                  <motion.span
                    className="block h-0.5 w-full bg-current"
                    animate={{
                      rotate: isMenuOpen ? -45 : 0,
                      translateY: isMenuOpen ? -8 : 0,
                    }}
                    transition={{ type: "spring", stiffness: 260, damping: 20 }}
                  />
                </div>
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Full Screen Mobile Menu - RENDERIZADO DIRECTO SIN PARPADEOS */}
      {isMenuOpen && (
        <>
          {/* Backdrop oscuro */}
          <div
            className="fixed inset-0 z-[9996] bg-black/60"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Menú fullscreen - Estilos mejorados */}
          <div
            className="fixed inset-0 z-[9997] flex flex-col justify-center items-center pt-20 pb-8 px-8 overflow-y-auto"
            style={{
              background:
                "linear-gradient(135deg, #023d35 0%, #014D43 50%, #026157 100%)",
              height: "calc(var(--vh, 1vh) * 100)",
              width: "100vw",
              paddingTop: "calc(env(safe-area-inset-top) + 80px)",
              paddingBottom: "calc(env(safe-area-inset-bottom) + 32px)",
              paddingLeft: "calc(env(safe-area-inset-left) + 32px)",
              paddingRight: "calc(env(safe-area-inset-right) + 32px)",
            }}
          >
            {/* Items del menú - Estilos mejorados */}
            <div className="flex flex-col items-center justify-center space-y-6 w-full flex-grow">
              {menuItems.map((item, index) => (
                <div
                  key={item.key}
                  className="w-full text-center"
                  style={{
                    animationDelay: `${index * 0.05}s`,
                  }}
                >
                  <Link
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className="block text-3xl md:text-4xl font-bold transition-all duration-300 hover:scale-110 py-2"
                    style={{
                      color: colors.mobileMenuText,
                      textShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                      letterSpacing: "0.01em",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#26A69A";
                      e.currentTarget.style.textShadow =
                        "0 0 20px rgba(38, 166, 154, 0.6)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = colors.mobileMenuText;
                      e.currentTarget.style.textShadow =
                        "0 2px 8px rgba(0, 0, 0, 0.3)";
                    }}
                  >
                    {item.label}
                  </Link>
                </div>
              ))}

              {/* Language Selector - Mejorado con francés */}
              <div
                className="flex justify-center space-x-3 pt-6 border-t w-full mt-4"
                style={{ borderColor: "rgba(255, 255, 255, 0.2)" }}
              >
                <button
                  onClick={() => handleLanguageChange("en")}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 ${
                    i18n.language === "en"
                      ? "bg-gradient-to-r from-white/30 to-white/20 text-white scale-105 shadow-md border border-white/40"
                      : "text-white/50 hover:text-white hover:bg-white/10 hover:scale-105 border border-transparent"
                  }`}
                >
                  🇬🇧 EN
                </button>
                <button
                  onClick={() => handleLanguageChange("es")}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 ${
                    i18n.language === "es"
                      ? "bg-gradient-to-r from-white/30 to-white/20 text-white scale-105 shadow-md border border-white/40"
                      : "text-white/50 hover:text-white hover:bg-white/10 hover:scale-105 border border-transparent"
                  }`}
                >
                  🇪🇸 ES
                </button>
                <button
                  onClick={() => handleLanguageChange("fr")}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 ${
                    i18n.language === "fr"
                      ? "bg-gradient-to-r from-white/30 to-white/20 text-white scale-105 shadow-md border border-white/40"
                      : "text-white/50 hover:text-white hover:bg-white/10 hover:scale-105 border border-transparent"
                  }`}
                >
                  🇫🇷 FR
                </button>
              </div>

              {/* Auth Buttons - Estilos mejorados */}
              <div className="flex flex-col w-full space-y-3 pt-4 max-w-sm">
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    navigate("/login");
                  }}
                  className="px-6 py-3 rounded-xl text-base font-semibold border-2 border-white/80 text-white hover:bg-white hover:text-[#023d35] transition-all duration-300 hover:scale-105 hover:shadow-xl backdrop-blur-sm bg-white/5"
                >
                  {t("buttons.login", "Sign In")}
                </button>
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    navigate("/signup");
                  }}
                  className="px-6 py-3 rounded-xl text-base font-semibold text-[#023d35] transition-all duration-300 hover:scale-105 hover:shadow-xl shadow-md"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(230, 240, 255, 1) 50%, rgba(220, 235, 255, 1) 100%)",
                  }}
                >
                  {t("buttons.signUp", "Sign Up")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MobileNavbar;
